/**
 * File-type policy — how an upload surface decides what it will store.
 *
 * A surface-neutral engine answering three questions about an uploaded file:
 *
 *   1. may we store it at all,
 *   2. what content type do we store, presign and serve for it,
 *   3. may that content type ever be served `Content-Disposition: inline`.
 *
 * Every upload surface on the platform faces these questions. Rather than each
 * one growing its own answer, a surface declares a {@link FileTypePolicy} — a
 * plain config object — and calls the shared resolver with it. Adding a surface
 * is a new policy constant, not new logic.
 *
 * It lives here, in a zero-dependency package both the API and the webapp
 * already depend on, precisely so there is ONE copy. The chat caps in
 * `client/src/lib/api.ts` are mirrored from `@fias/db-types` because that
 * package is server-only; this module has no such constraint and must not
 * acquire one — importing anything server-side here would force the webapp back
 * onto a duplicated copy.
 *
 * Related, deliberately separate: `@fias/security-rules`' `plugin-assets.ts`
 * governs files under a plugin's `public/` directory. It is far narrower (seven
 * binary formats) and does magic-byte validation this module does not, so the
 * two are cousins rather than one policy — folding them together would be a
 * behavior change, not a refactor.
 */

/**
 * One known file type.
 *
 * Extension-primary by design: browsers report wildly inconsistent MIME for the
 * same bytes (`.zip` arrives as `application/zip`, `application/x-zip-compressed`
 * or empty; `.csv` as `text/csv` or `application/vnd.ms-excel`; `.md` usually as
 * empty). The filename extension is the only stable signal both the client and
 * the server see, so it decides the type, and `canonicalMime` is what we store,
 * presign and serve — turning a client-controlled free-text field into a value
 * drawn from a closed set.
 */
export interface FileTypeEntry {
  /** Lowercase, no leading dot. Unique across the catalog. */
  readonly extensions: readonly string[];
  /** The MIME we store, sign and serve for this type. */
  readonly canonicalMime: string;
  /**
   * Other MIMEs browsers report for these bytes. Consulted ONLY when the
   * filename carries no recognized extension. Unique across the catalog,
   * canonical values included.
   */
  readonly aliasMimes?: readonly string[];
  /**
   * May be served `Content-Disposition: inline`. Absent means download-only —
   * the browser saves the file instead of rendering it.
   */
  readonly inlineSafe?: boolean;
}

/**
 * Known file types, shared across surfaces. A policy may expose a subset via
 * its own `catalog`; anything not listed here is still storable when the policy
 * says so (see {@link FileTypePolicy.unknownTypes}) — it just stores opaquely
 * and never renders inline.
 */
export const FILE_TYPE_CATALOG: readonly FileTypeEntry[] = [
  // --- documents ---
  {
    extensions: ['pdf'],
    canonicalMime: 'application/pdf',
    aliasMimes: ['application/x-pdf'],
    inlineSafe: true,
  },
  { extensions: ['txt', 'log'], canonicalMime: 'text/plain', inlineSafe: true },
  {
    extensions: ['md', 'markdown'],
    canonicalMime: 'text/markdown',
    aliasMimes: ['text/x-markdown'],
    inlineSafe: true,
  },
  { extensions: ['rtf'], canonicalMime: 'application/rtf', aliasMimes: ['text/rtf'] },
  { extensions: ['doc'], canonicalMime: 'application/msword' },
  {
    extensions: ['docx'],
    canonicalMime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  },
  { extensions: ['xls'], canonicalMime: 'application/vnd.ms-excel' },
  {
    extensions: ['xlsx'],
    canonicalMime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  },
  { extensions: ['xlsm'], canonicalMime: 'application/vnd.ms-excel.sheet.macroEnabled.12' },
  { extensions: ['ppt'], canonicalMime: 'application/vnd.ms-powerpoint' },
  {
    extensions: ['pptx'],
    canonicalMime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  },
  { extensions: ['odt'], canonicalMime: 'application/vnd.oasis.opendocument.text' },
  { extensions: ['ods'], canonicalMime: 'application/vnd.oasis.opendocument.spreadsheet' },
  { extensions: ['odp'], canonicalMime: 'application/vnd.oasis.opendocument.presentation' },
  { extensions: ['epub'], canonicalMime: 'application/epub+zip' },

  // --- mail & calendar ---
  { extensions: ['eml'], canonicalMime: 'message/rfc822' },
  { extensions: ['msg'], canonicalMime: 'application/vnd.ms-outlook' },
  { extensions: ['ics'], canonicalMime: 'text/calendar' },

  // --- structured data ---
  {
    extensions: ['csv'],
    canonicalMime: 'text/csv',
    aliasMimes: ['application/csv'],
    inlineSafe: true,
  },
  { extensions: ['tsv', 'tab'], canonicalMime: 'text/tab-separated-values', inlineSafe: true },
  // json/xml/yaml are download-only: browsers render them, and XML in particular
  // can carry external-entity / XSLT payloads.
  { extensions: ['json'], canonicalMime: 'application/json', aliasMimes: ['text/json'] },
  { extensions: ['xml'], canonicalMime: 'application/xml', aliasMimes: ['text/xml'] },
  {
    extensions: ['yaml', 'yml'],
    canonicalMime: 'application/yaml',
    aliasMimes: ['text/yaml', 'application/x-yaml', 'text/x-yaml'],
  },

  // --- archives ---
  {
    extensions: ['zip'],
    canonicalMime: 'application/zip',
    aliasMimes: ['application/x-zip-compressed', 'application/x-zip', 'multipart/x-zip'],
  },
  { extensions: ['7z'], canonicalMime: 'application/x-7z-compressed' },
  {
    extensions: ['rar'],
    canonicalMime: 'application/vnd.rar',
    aliasMimes: ['application/x-rar-compressed', 'application/x-rar'],
  },
  { extensions: ['tar'], canonicalMime: 'application/x-tar' },
  {
    extensions: ['gz', 'tgz'],
    canonicalMime: 'application/gzip',
    aliasMimes: ['application/x-gzip'],
  },
  {
    extensions: ['bz2', 'tbz2'],
    canonicalMime: 'application/x-bzip2',
    aliasMimes: ['application/x-bzip'],
  },
  { extensions: ['xz'], canonicalMime: 'application/x-xz' },
  { extensions: ['zst'], canonicalMime: 'application/zstd' },

  // --- images ---
  { extensions: ['png'], canonicalMime: 'image/png', inlineSafe: true },
  {
    extensions: ['jpg', 'jpeg'],
    canonicalMime: 'image/jpeg',
    aliasMimes: ['image/jpg'],
    inlineSafe: true,
  },
  { extensions: ['gif'], canonicalMime: 'image/gif', inlineSafe: true },
  { extensions: ['webp'], canonicalMime: 'image/webp', inlineSafe: true },
  {
    extensions: ['bmp'],
    canonicalMime: 'image/bmp',
    aliasMimes: ['image/x-ms-bmp'],
    inlineSafe: true,
  },
  { extensions: ['tif', 'tiff'], canonicalMime: 'image/tiff', inlineSafe: true },
  { extensions: ['heic'], canonicalMime: 'image/heic', inlineSafe: true },
  { extensions: ['heif'], canonicalMime: 'image/heif', inlineSafe: true },
  { extensions: ['avif'], canonicalMime: 'image/avif', inlineSafe: true },
  // SVG is accepted but deliberately NOT inline-safe: it is a scriptable
  // document, and bytes are served from a bucket origin. This explicit entry is
  // what keeps it from falling through to the media-family rule.
  { extensions: ['svg', 'svgz'], canonicalMime: 'image/svg+xml' },
  { extensions: ['psd'], canonicalMime: 'image/vnd.adobe.photoshop' },

  // --- audio / video ---
  { extensions: ['mp4', 'm4v'], canonicalMime: 'video/mp4', inlineSafe: true },
  { extensions: ['mov'], canonicalMime: 'video/quicktime', inlineSafe: true },
  { extensions: ['webm'], canonicalMime: 'video/webm', inlineSafe: true },
  { extensions: ['mkv'], canonicalMime: 'video/x-matroska', inlineSafe: true },
  { extensions: ['avi'], canonicalMime: 'video/x-msvideo', inlineSafe: true },
  { extensions: ['wmv'], canonicalMime: 'video/x-ms-wmv', inlineSafe: true },
  {
    extensions: ['mp3'],
    canonicalMime: 'audio/mpeg',
    aliasMimes: ['audio/mp3'],
    inlineSafe: true,
  },
  {
    extensions: ['m4a'],
    canonicalMime: 'audio/mp4',
    aliasMimes: ['audio/x-m4a'],
    inlineSafe: true,
  },
  { extensions: ['aac'], canonicalMime: 'audio/aac', inlineSafe: true },
  {
    extensions: ['wav'],
    canonicalMime: 'audio/wav',
    aliasMimes: ['audio/x-wav', 'audio/wave', 'audio/vnd.wave'],
    inlineSafe: true,
  },
  { extensions: ['ogg', 'oga'], canonicalMime: 'audio/ogg', inlineSafe: true },
  { extensions: ['opus'], canonicalMime: 'audio/opus', inlineSafe: true },
  {
    extensions: ['flac'],
    canonicalMime: 'audio/flac',
    aliasMimes: ['audio/x-flac'],
    inlineSafe: true,
  },
  { extensions: ['wma'], canonicalMime: 'audio/x-ms-wma', inlineSafe: true },
];

/**
 * Extensions no surface stores — executables, scripts, installers and active
 * web content. Checked FIRST, so a double-extension like `invoice.pdf.exe` is
 * rejected on its trailing extension.
 */
export const EXECUTABLE_EXTENSIONS: ReadonlySet<string> = new Set([
  // native executables & installers
  'exe',
  'dll',
  'com',
  'msi',
  'msix',
  'scr',
  'cpl',
  'pif',
  'sys',
  'so',
  'dylib',
  'app',
  'dmg',
  'pkg',
  'deb',
  'rpm',
  'apk',
  'jar',
  'class',
  'bin',
  'run',
  'iso',
  // shell & script
  'bat',
  'cmd',
  'vbs',
  'vbe',
  'wsf',
  'wsh',
  'hta',
  'reg',
  'lnk',
  'ps1',
  'psm1',
  'sh',
  'bash',
  'zsh',
  'csh',
  'ksh',
  'py',
  'pyc',
  'rb',
  'pl',
  'php',
  'js',
  'mjs',
  'cjs',
  'jse',
  'ts',
  'scpt',
  'gadget',
  'chm',
  'swf',
  // active web content — renders and executes in a browser tab
  'html',
  'htm',
  'xhtml',
  'shtml',
  'mht',
  'mhtml',
  'xsl',
  'xslt',
]);

/**
 * MIMEs never accepted, even when the extension looks benign or the media-family
 * rule would otherwise pass them.
 */
export const ACTIVE_CONTENT_MIMES: ReadonlySet<string> = new Set([
  'text/html',
  'application/xhtml+xml',
  'application/x-msdownload',
  'application/x-msdos-program',
  'application/x-sh',
  'application/x-shellscript',
  'application/javascript',
  'text/javascript',
  'application/x-httpd-php',
]);

/**
 * What we store for a file we accept but do not recognize. Deliberately the
 * most boring type there is: browsers download it rather than render it, so an
 * unknown format costs the user nothing but can never become an inline render.
 */
export const OPAQUE_BINARY_MIME = 'application/octet-stream';

/**
 * How one upload surface answers the three questions above.
 *
 * `unknownTypes` is the interesting knob:
 *   • `'store-opaque'` — accept an unrecognized-but-not-blocked file and store
 *     it as {@link OPAQUE_BINARY_MIME}, download-only. The surface stays open to
 *     formats nobody enumerated (`.dwg`, `.sketch`, next year's camera raw)
 *     without ever widening what may render inline. Adding a catalog entry later
 *     is a pure UX upgrade (nicer icon, inline preview), never an unblocking.
 *   • `'reject'` — refuse anything unrecognized. Only appropriate where a
 *     downstream pipeline can genuinely only consume known formats.
 */
export interface FileTypePolicy {
  /** Surface name, for error messages and debugging. */
  readonly name: string;
  /** Types this surface recognizes. Usually {@link FILE_TYPE_CATALOG}. */
  readonly catalog: readonly FileTypeEntry[];
  /** Extensions refused outright. */
  readonly blockedExtensions: ReadonlySet<string>;
  /** MIMEs refused outright. */
  readonly blockedMimes: ReadonlySet<string>;
  /** What to do with an unrecognized, non-blocked file. */
  readonly unknownTypes: 'reject' | 'store-opaque';
  /**
   * Accept any well-formed `image/`, `audio/` or `video/` subtype the catalog
   * did not enumerate, and treat it as inline-safe. Keeps an `accept="image/*"`
   * picker from silently narrowing on exotic camera and codec types.
   */
  readonly allowMediaFamilyRule: boolean;
}

/**
 * Chat attachments. Open to any format a user wants to share, closed to
 * anything that executes, and inline only for types we have vetted — chat
 * attachments are served from a bucket origin where a rendered document would
 * run with that origin's privileges.
 */
export const CHAT_FILE_TYPE_POLICY: FileTypePolicy = {
  name: 'chat',
  catalog: FILE_TYPE_CATALOG,
  blockedExtensions: EXECUTABLE_EXTENSIONS,
  blockedMimes: ACTIVE_CONTENT_MIMES,
  unknownTypes: 'store-opaque',
  allowMediaFamilyRule: true,
};

/** Why a file was refused. */
export type FileTypeRejection = 'blocked-type' | 'unsupported-type';

/** The canonical type a file resolved to. */
export interface FileTypeResolution {
  /** Canonical MIME to store, presign and serve. */
  mimeType: string;
  /** Whether this type may ever be served inline. */
  inlineSafe: boolean;
  /** False when the type fell through to {@link OPAQUE_BINARY_MIME}. */
  recognized: boolean;
}

export type FileTypeResult =
  | { ok: true; type: FileTypeResolution }
  | { ok: false; reason: FileTypeRejection };

/**
 * Well-formed `image/`, `audio/` or `video/` subtype.
 *
 * The `+xml`-suffixed and bare `xml` subtypes are excluded on purpose: they are
 * the scriptable corner of the media families (`image/svg+xml` is the one
 * everybody knows),
 * and the family rule's whole job is to wave through *ordinary* media it has
 * never heard of. An excluded value is not rejected — it falls through to the
 * unknown-type lane and stores opaquely, download-only.
 */
const MEDIA_MIME_RE = /^(image|audio|video)\/[a-z0-9][a-z0-9!#$&^_.+-]{0,126}$/;
const XML_MIME_RE = /(\+xml|\/xml)$/;

/**
 * Lowercased, dotless extension, or '' when the name has none.
 *
 * Trailing dots and whitespace are stripped BEFORE the extension is read:
 * Windows silently drops them when saving, so `evil.exe.` lands on disk as
 * `evil.exe` — without the strip it would read as having no extension and slip
 * past a blocklist that catches `evil.exe`.
 */
export function fileExtension(fileName: string): string {
  const match = /\.([A-Za-z0-9]{1,12})$/.exec(fileName.trim().replace(/[.\s]+$/, ''));
  return match ? match[1].toLowerCase() : '';
}

/** Strips parameters (`; charset=utf-8`) and lowercases. */
function normalizeMime(mimeType: string): string {
  return (mimeType || '').split(';')[0].trim().toLowerCase();
}

interface CatalogIndex {
  byExtension: Map<string, FileTypeEntry>;
  byMime: Map<string, FileTypeEntry>;
}

/**
 * Extension -> entry and MIME -> entry lookups, built lazily per catalog and
 * cached. A duplicate key would silently resolve last-wins; the catalog is
 * static data, so that is asserted by the unit tests rather than guarded at
 * runtime — a module-load `throw` here would take down the whole webapp bundle
 * (and every backend service) over a typo a test already catches.
 */
const CATALOG_INDEXES = new WeakMap<readonly FileTypeEntry[], CatalogIndex>();

function indexFor(catalog: readonly FileTypeEntry[]): CatalogIndex {
  const cached = CATALOG_INDEXES.get(catalog);
  if (cached) return cached;
  const index: CatalogIndex = { byExtension: new Map(), byMime: new Map() };
  for (const entry of catalog) {
    for (const ext of entry.extensions) index.byExtension.set(ext, entry);
    for (const mime of [entry.canonicalMime, ...(entry.aliasMimes ?? [])]) {
      index.byMime.set(mime, entry);
    }
  }
  CATALOG_INDEXES.set(catalog, index);
  return index;
}

function resolutionFor(entry: FileTypeEntry): FileTypeResult {
  return {
    ok: true,
    type: {
      mimeType: entry.canonicalMime,
      inlineSafe: entry.inlineSafe === true,
      recognized: true,
    },
  };
}

/**
 * Resolves a (fileName, declaredMimeType) pair to the canonical type the given
 * surface will store, or a rejection reason.
 *
 * NOT a malware boundary — both inputs are client-supplied and nothing here
 * reads a byte, so a renamed executable still gets through. What it guarantees
 * is that the stored MIME comes from a closed set (a catalog canonical, a
 * vetted media subtype, or {@link OPAQUE_BINARY_MIME}), which is what makes the
 * inline-vs-download decision on the read path sound.
 */
export function resolveFileType(
  fileName: string,
  declaredMimeType: string,
  policy: FileTypePolicy,
): FileTypeResult {
  const ext = fileExtension(fileName);
  const mime = normalizeMime(declaredMimeType);

  // 1. Blocklists win over everything else, including the family rule.
  if (ext && policy.blockedExtensions.has(ext)) return { ok: false, reason: 'blocked-type' };
  if (mime && policy.blockedMimes.has(mime)) return { ok: false, reason: 'blocked-type' };

  const index = indexFor(policy.catalog);

  // 2. The extension decides, ignoring whatever the browser guessed.
  const byExtension = ext ? index.byExtension.get(ext) : undefined;
  if (byExtension) return resolutionFor(byExtension);

  // 3. No recognized extension — fall back to a known canonical or alias MIME.
  const byMime = mime ? index.byMime.get(mime) : undefined;
  if (byMime) return resolutionFor(byMime);

  // 4. Family rule — an un-enumerated but well-formed media subtype is still
  //    ordinary media (camera formats, exotic codecs).
  if (policy.allowMediaFamilyRule && MEDIA_MIME_RE.test(mime) && !XML_MIME_RE.test(mime)) {
    return { ok: true, type: { mimeType: mime, inlineSafe: true, recognized: true } };
  }

  // 5. Unrecognized. Either the surface refuses it, or it stores opaquely and
  //    is never rendered.
  if (policy.unknownTypes === 'reject') return { ok: false, reason: 'unsupported-type' };
  return {
    ok: true,
    type: { mimeType: OPAQUE_BINARY_MIME, inlineSafe: false, recognized: false },
  };
}

/**
 * Whether a STORED mime may be served `Content-Disposition: inline`.
 *
 * Takes only a mime because rows written before a policy existed have no
 * catalog entry — those fail closed and become download-only, which is the
 * point.
 */
export function isMimeInlineSafe(mimeType: string, policy: FileTypePolicy): boolean {
  const mime = normalizeMime(mimeType);
  if (!mime || policy.blockedMimes.has(mime)) return false;
  const entry = indexFor(policy.catalog).byMime.get(mime);
  if (entry) return entry.inlineSafe === true;
  return policy.allowMediaFamilyRule && MEDIA_MIME_RE.test(mime) && !XML_MIME_RE.test(mime);
}

/**
 * `accept` attribute for a file picker constrained to this policy's catalog.
 *
 * Extensions are listed alongside MIMEs because some platforms match only one
 * or the other. `excludeMedia` drops the image/audio/video families for a
 * surface that offers them through a separate picker entry.
 *
 * Note this is narrower than what the policy will actually *accept* under
 * `unknownTypes: 'store-opaque'` — the `accept` attribute is a convenience
 * filter on the OS dialog, not the gate.
 */
export function buildFileAcceptAttribute(
  policy: FileTypePolicy,
  options: { excludeMedia?: boolean } = {},
): string {
  const parts: string[] = [];
  for (const entry of policy.catalog) {
    if (options.excludeMedia && MEDIA_MIME_RE.test(entry.canonicalMime)) continue;
    for (const ext of entry.extensions) parts.push(`.${ext}`);
    parts.push(entry.canonicalMime);
  }
  return parts.join(',');
}
